import React, { useState } from "react";
import { toast } from "sonner";
import { Archive, ArchiveRestore, Check, Loader2, MoreHorizontal, Send, Trash2, X } from "lucide-react";
import { api, apiError } from "../lib/api";
import { can } from "../lib/permissions";
import { fmtDateTime } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NOUN = { archive: "archiviazione", restore: "ripristino", delete: "eliminazione" };
const APPROVER = { archive: "vehicles.archive", restore: "vehicles.archive", delete: "vehicles.delete" };

export const pendingRequest = (vehicle) => (vehicle?.lifecycle_requests || []).find((r) => r.status === "pending");

// What this person may do on this vehicle, from the server permissions.
export function lifecycleOptions(user, vehicle) {
  const archived = vehicle?.is_archived === true;
  const ask = can(user, "vehicles.request_lifecycle") && !pendingRequest(vehicle);
  const direct = can(user, "vehicles.archive");
  return [
    !archived && (direct ? { kind: "archive", direct: true } : ask && { kind: "archive", direct: false }),
    archived && (direct ? { kind: "restore", direct: true } : ask && { kind: "restore", direct: false }),
    can(user, "vehicles.delete") ? { kind: "delete", direct: true } : ask && { kind: "delete", direct: false },
  ].filter(Boolean);
}

const LABEL = {
  archive: ["Archivia", "Chiedi archiviazione"],
  restore: ["Ripristina", "Chiedi ripristino"],
  delete: ["Elimina definitivamente", "Chiedi eliminazione"],
};
const ICON = { archive: Archive, restore: ArchiveRestore, delete: Trash2 };

/** Header menu with archive / restore / delete, or the request for them. */
export default function VehicleLifecycle({ user, vehicle, onChanged, onDeleted }) {
  const [open, setOpen] = useState(null); // {kind, direct}
  const [reason, setReason] = useState("");
  const [plate, setPlate] = useState("");
  const [busy, setBusy] = useState(false);
  const options = lifecycleOptions(user, vehicle);
  if (!options.length) return null;
  const headers = { headers: { "If-Match": vehicle.updated_at || "" } };
  const start = (option) => { setReason(""); setPlate(""); setOpen(option); };

  const submit = async () => {
    setBusy(true);
    try {
      const { kind, direct } = open;
      if (!direct) {
        const { data } = await api.post(`/vehicles/${vehicle.id}/requests`, { action: kind, reason }, headers);
        toast.success(`Richiesta di ${NOUN[kind]} inviata`);
        onChanged(data);
      } else if (kind === "delete") {
        await api.post(`/vehicles/${vehicle.id}/purge`, { confirm_plate: plate }, headers);
        toast.success(`${vehicle.plate} eliminato`);
        onDeleted();
      } else {
        const { data } = kind === "archive"
          ? await api.delete(`/vehicles/${vehicle.id}`, headers)
          : await api.post(`/vehicles/${vehicle.id}/restore`, {}, headers);
        toast.success(`${vehicle.plate} ${kind === "archive" ? "archiviato" : "ripristinato"}`);
        onChanged(data);
      }
      setOpen(null);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const valid = !open ? false : !open.direct ? reason.trim().length > 0
    : open.kind === "delete" ? plate.trim().toUpperCase() === String(vehicle.plate || "").toUpperCase() : true;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" aria-label="Altre azioni sul veicolo" data-testid="vehicle-lifecycle-menu"><MoreHorizontal /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {options.map((option, index) => {
            const Icon = ICON[option.kind];
            return (
              <React.Fragment key={option.kind}>
                {option.kind === "delete" && index > 0 && <DropdownMenuSeparator />}
                <DropdownMenuItem onSelect={() => start(option)} data-testid={`vehicle-${option.kind}-${option.direct ? "direct" : "request"}`}
                  className={`min-h-10 ${option.kind === "delete" && option.direct ? "text-destructive focus:text-destructive" : ""}`}>
                  <Icon className="mr-2 h-4 w-4" aria-hidden="true" />{LABEL[option.kind][option.direct ? 0 : 1]}
                </DropdownMenuItem>
              </React.Fragment>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!open} onOpenChange={(value) => { if (!busy && !value) setOpen(null); }}>
        <DialogContent className="sm:max-w-md">
          {open && <>
            <DialogHeader>
              <DialogTitle>{open.direct ? `${LABEL[open.kind][0]} ${vehicle.plate}?` : `${LABEL[open.kind][1]} di ${vehicle.plate}`}</DialogTitle>
              <DialogDescription>
                {!open.direct ? "La richiesta arriva a chi può decidere. Il veicolo non cambia finché non viene approvata."
                  : open.kind === "archive" ? "Il veicolo esce dagli elenchi e risulta venduto. Noleggi, costi, verbali e report restano. Potrai ripristinarlo da Veicoli → Archiviati."
                  : open.kind === "restore" ? "Il veicolo torna negli elenchi con lo stato che aveva prima dell'archiviazione."
                  : "Il veicolo viene cancellato per sempre. È possibile solo se non ha storia (noleggi, preventivi, tariffe, manutenzioni, scadenze, costi, rifornimenti, danni, multe): altrimenti va archiviato."}
              </DialogDescription>
            </DialogHeader>
            {!open.direct && <div className="space-y-2">
              <Label htmlFor="lifecycle-reason">Motivo</Label>
              <Textarea id="lifecycle-reason" value={reason} maxLength={1000} rows={3} onChange={(e) => setReason(e.target.value)} placeholder="Es. veicolo venduto il 15/11" />
            </div>}
            {open.direct && open.kind === "delete" && <div className="space-y-2">
              <Label htmlFor="lifecycle-plate">Scrivi la targa <strong>{vehicle.plate}</strong> per confermare</Label>
              <Input id="lifecycle-plate" value={plate} autoComplete="off" onChange={(e) => setPlate(e.target.value)} data-testid="vehicle-delete-plate" />
            </div>}
            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => setOpen(null)}>Annulla</Button>
              <Button variant={open.direct && open.kind !== "restore" ? "destructive" : "default"} disabled={busy || !valid} onClick={submit} data-testid="vehicle-lifecycle-confirm">
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : !open.direct && <Send aria-hidden="true" />}
                {open.direct ? LABEL[open.kind][0] : "Invia richiesta"}
              </Button>
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Banner for an archived vehicle and for a request waiting on it, with approve / reject for who decides. */
export function VehicleLifecycleNotice({ user, vehicle, onChanged, onDeleted }) {
  const request = pendingRequest(vehicle);
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const archived = vehicle?.is_archived === true;
  // The person who asked sees how their latest request ended, until they ask again.
  const latest = (vehicle?.lifecycle_requests || []).slice(-1)[0];
  const answered = !request && latest?.status === "rejected" && latest.requester_id === user?.id ? latest : null;
  if (!archived && !request && !answered) return null;
  const mayDecide = request && can(user, APPROVER[request.action]) && request.requester_id !== user.id;

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vehicles/${vehicle.id}/requests/${request.id}/decision`,
        { decision, reason }, { headers: { "If-Match": vehicle.updated_at || "" } });
      toast.success(decision === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDecision(null);
      if (data?.deleted) onDeleted(); else onChanged(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="vehicle-lifecycle-notice">
      {archived && <div role="status" className="rounded-xl border border-border bg-secondary/60 p-4 text-sm">
        <p className="font-semibold">Veicolo archiviato</p>
        <p className="text-muted-foreground">{[vehicle.archived_at && `Il ${fmtDateTime(vehicle.archived_at)}`, vehicle.archived_by_name && `da ${vehicle.archived_by_name}`].filter(Boolean).join(" ") || "Non compare negli elenchi."}{vehicle.archived_reason ? ` · ${vehicle.archived_reason}` : ""}</p>
      </div>}
      {answered && <div role="status" className="rounded-xl border border-border bg-secondary/60 p-4 text-sm" data-testid="vehicle-request-answered">
        <p className="font-semibold">La tua richiesta di {NOUN[answered.action]} è stata rifiutata</p>
        <p className="break-words text-muted-foreground">{answered.decided_by_name || "Responsabile"}, {fmtDateTime(answered.decided_at)} · «{answered.decision_reason}»</p>
      </div>}
      {request && <div role="status" className="tone-amber flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-sm" style={{ borderColor: "var(--tone-line)", background: "var(--tone-fill)" }}>
        <div className="min-w-0">
          <p className="font-semibold" style={{ color: "var(--tone-ink)" }}>Richiesta di {NOUN[request.action]} in attesa</p>
          <p className="break-words">«{request.reason}» · {request.requester_name || "Utente"}, {fmtDateTime(request.created_at)}</p>
          {!mayDecide && <p className="text-muted-foreground">Decide chi può {request.action === "delete" ? "eliminare" : "archiviare"} i veicoli.</p>}
        </div>
        {mayDecide && <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => { setReason(""); setDecision("reject"); }} data-testid="vehicle-request-reject"><X aria-hidden="true" />Rifiuta</Button>
          <Button size="sm" onClick={() => { setReason(""); setDecision("approve"); }} data-testid="vehicle-request-approve"><Check aria-hidden="true" />Approva</Button>
        </div>}
      </div>}
      <Dialog open={!!decision} onOpenChange={(value) => { if (!busy && !value) setDecision(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{decision === "approve" ? `Approvare la ${NOUN[request?.action]}?` : "Rifiutare la richiesta?"}</DialogTitle>
            <DialogDescription>{decision === "approve" ? `${vehicle.plate} viene ${request?.action === "delete" ? "eliminato per sempre" : request?.action === "restore" ? "ripristinato" : "archiviato"} subito.` : "Il veicolo resta com'è; chi ha chiesto vede il motivo."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="decision-reason">Motivo</Label>
            <Textarea id="decision-reason" value={reason} maxLength={1000} rows={3} onChange={(e) => setReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={busy} onClick={() => setDecision(null)}>Annulla</Button>
            <Button variant={decision === "approve" && request?.action === "delete" ? "destructive" : "default"} disabled={busy || !reason.trim()} onClick={send} data-testid="vehicle-decision-confirm">
              {busy && <Loader2 className="animate-spin" aria-hidden="true" />}{decision === "approve" ? "Approva" : "Rifiuta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
